import React, { useState } from 'react';
import { 
  FileText, Download, Filter, Clock, 
  BarChart2, Save, Play, Edit2, Copy,
  Trash2, Calendar, Share2, Eye
} from 'lucide-react';

const CustomReporting = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [reportFormat, setReportFormat] = useState('pdf');

  // Sample report templates
  const reportTemplates = [
    {
      id: 'T001',
      name: 'SMT Risk Overview',
      description: 'Monthly overview of all risks for SMT review',
      type: 'standard',
      lastRun: '2024-03-15',
      schedule: 'Monthly',
      fields: [
        'riskId', 'title', 'category', 'status', 
        'owner', 'mitigationStatus', 'lastReview'
      ],
      filters: [
        { field: 'status', operator: 'in', values: ['high', 'medium'] }
      ]
    },
    {
      id: 'T002',
      name: 'GB Compliance Report',
      description: 'Quarterly compliance risk report for GB',
      type: 'compliance',
      lastRun: '2024-03-01',
      schedule: 'Quarterly',
      fields: [
        'riskId', 'title', 'compliance_status', 
        'mitigation_progress', 'next_review'
      ],
      filters: [
        { field: 'category', operator: 'equals', value: 'regulatory' }
      ]
    }
  ];

  // Available fields for reports
  const availableFields = {
    risk: [
      { id: 'riskId', label: 'Risk ID', group: 'basic' },
      { id: 'title', label: 'Title', group: 'basic' },
      { id: 'description', label: 'Description', group: 'basic' },
      { id: 'category', label: 'Category', group: 'basic' },
      { id: 'status', label: 'Status', group: 'basic' },
      { id: 'owner', label: 'Owner', group: 'basic' },
      { id: 'createdDate', label: 'Created Date', group: 'dates' },
      { id: 'lastReview', label: 'Last Review', group: 'dates' },
      { id: 'nextReview', label: 'Next Review', group: 'dates' },
      { id: 'mitigationStatus', label: 'Mitigation Status', group: 'mitigation' },
      { id: 'mitigationProgress', label: 'Mitigation Progress', group: 'mitigation' },
      { id: 'approvalStatus', label: 'Approval Status', group: 'approval' },
      { id: 'lastApproval', label: 'Last Approval', group: 'approval' },
      { id: 'riskScore', label: 'Risk Score', group: 'assessment' },
      { id: 'likelihood', label: 'Likelihood', group: 'assessment' },
      { id: 'impact', label: 'Impact', group: 'assessment' }
    ]
  };

  const TemplateCard = ({ template }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{template.name}</h3>
              <div className="text-sm text-gray-500">{template.description}</div>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setSelectedTemplate(template)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <Copy className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <Play className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              Last run: {template.lastRun}
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {template.schedule}
            </div>
          </div>

          {isExpanded && (
            <div className="mt-4 pt-4 border-t">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Included Fields</h4>
                  <div className="flex flex-wrap gap-2">
                    {template.fields.map(field => (
                      <span 
                        key={field}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Applied Filters</h4>
                  <div className="space-y-2">
                    {template.filters.map((filter, index) => (
                      <div key={index} className="text-sm text-gray-600">
                        {filter.field} {filter.operator} {filter.value || filter.values.join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-4 text-sm text-gray-500 hover:text-gray-700"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        </div>
      </div>
    );
  };

  const ReportBuilder = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4">Report Builder</h2>

        {/* Field Selection */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Select Fields</h3>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(availableFields).map(([group, fields]) => (
              <div key={group} className="space-y-2">
                {fields.map(field => (
                  <label key={field.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(field.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFields([...selectedFields, field.id]);
                        } else {
                          setSelectedFields(selectedFields.filter(f => f !== field.id));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-600">{field.label}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Format Options */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Report Format</h3>
          <div className="flex space-x-4">
            {['pdf', 'excel', 'csv'].map(format => (
              <label key={format} className="inline-flex items-center">
                <input
                  type="radio"
                  value={format}
                  checked={reportFormat === format}
                  onChange={(e) => setReportFormat(e.target.value)}
                  className="text-blue-600 border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-600">
                  {format.toUpperCase()}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <Save className="w-4 h-4 mr-2" />
            Save Template
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
            <Play className="w-4 h-4 mr-2" />
            Generate Report
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Custom Reporting</h1>
        <div className="text-gray-600">
          Create and manage custom reports
        </div>
      </div>

      {/* Actions */}
      <div className="mb-6 flex justify-between items-center">
        <div className="space-x-4">
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
            <FileText className="w-4 h-4 mr-2" />
            New Report
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <Share2 className="w-4 h-4 mr-2" />
            Share Templates
          </button>
        </div>
        <div className="flex items-center space-x-4">
          <select className="px-4 py-2 border rounded-md text-sm">
            <option value="all">All Templates</option>
            <option value="my">My Templates</option>
            <option value="shared">Shared Templates</option>
          </select>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <Download className="w-4 h-4 mr-2" />
            Download All
          </button>
        </div>
      </div>

      {/* Report Templates Grid */}
      <div className="mb-8 grid grid-cols-2 gap-4">
        {reportTemplates.map(template => (
          <TemplateCard key={template.id} template={template} />
        ))}
      </div>

      {/* Report Builder */}
      <ReportBuilder />
    </div>
  );
};

export default CustomReporting;
